import React from 'react';
import { Music, Video, Mic, Instagram, Youtube, Mail, MapPin, Heart } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    servicos: [
      { name: 'Produção Musical', href: '#servicos' },
      { name: 'Gravação de Podcast', href: '#servicos' },
      { name: 'Vídeoclipes', href: '#servicos' },
      { name: 'Cursos Online', href: '#servicos' },
    ],
    empresa: [
      { name: 'Quem Somos', href: '#quem-somos' },
      { name: 'O Estúdio', href: '#estudio' },
      { name: 'Trabalhos', href: '#trabalhos' },
      { name: 'Depoimentos', href: '#depoimentos' },
    ],
    contato: [
      { name: '61 98553-3770', href: 'https://wa.me/5561985533770', icon: Mail },
      { name: 'info@estudiohertz.com.br', href: 'mailto:info@estudiohertz.com.br', icon: Mail },
      { name: '@estudiohertz', href: 'https://www.instagram.com/estudiohertz/', icon: Instagram },
      { name: 'SRTV sul 701 Bloco I', href: 'https://maps.google.com', icon: MapPin },
    ],
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/estudiohertz/', label: 'Instagram' },
    { icon: Youtube, href: 'https://www.youtube.com/@papohertz', label: 'YouTube' },
  ];

  return (
    <footer className="bg-gradient-to-b from-[#1e3a5f] to-[#0f1d3a] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#1e3a5f] font-bold text-2xl">HZ</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#ff6b35] rounded-full"></div>
              </div>
              <div>
                <div className="text-white font-bold text-lg">ESTÚDIO HERTZ</div>
                <div className="text-[#ff6b35] text-xs font-medium">AUDIOVISUAL</div>
              </div>
            </div>
            <p className="text-white/80 leading-relaxed mb-6">
              Ecossistema de criação onde ideias ganham forma, som e impacto.
            </p>
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-[#ff6b35] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                  aria-label={social.label}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="font-bold text-lg mb-6 flex items-center">
              <Music className="mr-2 text-[#ff6b35]" size={20} />
              Serviços
            </h3>
            <ul className="space-y-3">
              {footerLinks.servicos.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-[#ff6b35] transition-colors duration-200 flex items-center group"
                  >
                    <span className="w-1 h-1 bg-[#ff6b35] rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-bold text-lg mb-6 flex items-center">
              <Video className="mr-2 text-[#ff6b35]" size={20} />
              Empresa
            </h3>
            <ul className="space-y-3">
              {footerLinks.empresa.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-[#ff6b35] transition-colors duration-200 flex items-center group"
                  >
                    <span className="w-1 h-1 bg-[#ff6b35] rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="font-bold text-lg mb-6 flex items-center">
              <Mic className="mr-2 text-[#ff6b35]" size={20} />
              Contato
            </h3>
            <ul className="space-y-4">
              {footerLinks.contato.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-[#ff6b35] transition-colors duration-200 flex items-start group text-sm"
                  >
                    <link.icon className="mr-2 text-[#ff6b35] flex-shrink-0 mt-0.5" size={16} />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="text-white/60 text-sm text-center md:text-left">
              <p>© {currentYear} Estúdio Hertz. Todos os direitos reservados.</p>
            </div>

            {/* Developer Credit */}
            <div className="flex items-center space-x-2 text-white/60 text-sm">
              <span>Desenvolvido com</span>
              <Heart className="text-[#ff6b35]" size={14} fill="currentColor" />
              <span>por</span>
              <a
                href="https://www.instagram.com/marianojunior"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ff6b35] hover:text-[#ff8555] font-semibold transition-colors duration-200"
              >
                Mariano Júnior
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;